import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, RightOutlined } from "@ant-design/icons";
import welcomeBg from "../../assets/homebg.png";
import mealApiService from "../../services/MealApiService";
import BottomNavBar from "../../components/BottomNavBar";

interface FoodItem {
  id: number | string;
  name: string;
  calorie_content_kcal: number;
  calories?: number; // Legacy field for compatibility
  fat_content_g: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  net_carbs_g: number;
  dietary_fiber_g: number;
  GI_estimate: number;
  GL_estimate: number;
  protein_content_g: number;
  image_url?: string;
  created_on?: string;
  created_at?: string;
  updated_at?: string;
  timestamp?: string;
  uploaded_at?: string;
  date_added?: string;
  [key: string]: any;
}

const MealDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const mealId = location.state?.mealId;
  const mealType = location.state?.mealType; // Meal type from Mealpage
  const mealTotals = location.state?.mealTotals; // Totals for the meal type
  const mealItems = useMemo(
    () => location.state?.mealItems ?? [],
    [location.state?.mealItems]
  );
  const mealImage = location.state?.mealImage;
  const mealDate = location.state?.mealDate;
  const fromMealPage = location.state?.fromMealPage;

  const [foodItems, setFoodItems] = useState<FoodItem[]>(mealItems);
  const [filteredItems, setFilteredItems] = useState<FoodItem[]>(mealItems);

  // Fetch meal data with React Query (only if not from MealPage)
  const { isLoading: loading } = useQuery({
    queryKey: ["mealItems", mealId],
    queryFn: async () => {
      if (fromMealPage && mealItems && mealItems.length > 0) {
        setFoodItems(mealItems);
        setFilteredItems(mealItems);
        return mealItems;
      }

      if (!mealId) {
        return [];
      }

      try {
        const response = await mealApiService.getFoodItemsByMealId(mealId);
        setFoodItems(response as any);
        setFilteredItems(response as any);
        return response;
      } catch (_error) {
        throw new Error("Failed to load meal items");
      }
    },
    enabled: !fromMealPage || (fromMealPage && mealItems.length === 0), // Only fetch if needed
  });
  const [error, setError] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Calculate totals from frontend food items
  const calculatedTotals = useMemo(() => {
    if (!foodItems || foodItems.length === 0) {
      return null;
    }

    const totals = foodItems.reduce(
      (acc, item) => ({
        calorie_content_kcal:
          acc.calorie_content_kcal +
          (item.calorie_content_kcal || item.calories || 0),
        carbohydrate_content_g:
          acc.carbohydrate_content_g +
          (item.net_carbs_g || 0) +
          (item.dietary_fiber_g || 0),
        protein_content_g:
          acc.protein_content_g + (item.protein_content_g || 0),
        fat_content_g: acc.fat_content_g + (item.fat_content_g || 0),
        trans_fat_g: acc.trans_fat_g + (item.trans_fat_g || 0),
        saturated_fat_g: acc.saturated_fat_g + (item.saturated_fat_g || 0),
        net_carbs_g: acc.net_carbs_g + (item.net_carbs_g || 0),
        dietary_fiber_g: acc.dietary_fiber_g + (item.dietary_fiber_g || 0),
        sugar_g: acc.sugar_g + (item.sugar_g || 0),
        GI_estimate: acc.GI_estimate + (item.GI_estimate || 0),
        GL_estimate:
          acc.GL_estimate +
          (item.GL_estimate ||
            ((item.GI_estimate || 0) * (item.net_carbs_g || 0)) / 100),
      }),
      {
        calorie_content_kcal: 0,
        carbohydrate_content_g: 0,
        protein_content_g: 0,
        fat_content_g: 0,
        trans_fat_g: 0,
        saturated_fat_g: 0,
        net_carbs_g: 0,
        dietary_fiber_g: 0,
        sugar_g: 0,
        GI_estimate: 0,
        GL_estimate: 0,
      }
    );

    // GI and GL are both simple sums
    return {
      ...totals,
      GI_estimate: totals.GI_estimate,
      GL_estimate: totals.GL_estimate,
    };
  }, [foodItems]);

  // Use calculated totals instead of passed totals
  const displayTotals = calculatedTotals || mealTotals;

  useEffect(() => {
    setFilteredItems(foodItems);
  }, [foodItems]);

  const handleItemClick = (itemIndex: number) => {
    // If clicking on already expanded item, close it
    // If clicking on different item, close previous and open new one
    setExpandedItem(expandedItem === itemIndex ? null : itemIndex);
  };

  const formatTime = (item: FoodItem) => {
    const dateFields = [
      "uploaded_at",
      "created_at",
      "timestamp",
      "date_added",
      "updated_at",
      "created_on",
    ];

    // Find the first available date field
    for (const field of dateFields) {
      if (item[field] && item[field].toString().trim() !== "") {
        const rawTime = item[field].toString();
        return convertTo12HourFormat(rawTime);
      }
    }

    // If no date field found in item, try mealDate
    if (mealDate && mealDate.toString().trim() !== "") {
      return convertTo12HourFormat(mealDate.toString());
    }

    // Fallback to current time in 12-hour format
    return new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const convertTo12HourFormat = (dateTimeString: string) => {
    try {
      let timeString = "";

      // Extract time part from different formats
      if (dateTimeString.includes(" ") && dateTimeString.includes(":")) {
        timeString = dateTimeString.split(" ")[1]; // "2025-11-26 14:30:00" format
      } else if (dateTimeString.includes("T") && dateTimeString.includes(":")) {
        timeString = dateTimeString.split("T")[1].split("Z")[0]; // ISO format
      } else if (
        dateTimeString.includes(":") &&
        !dateTimeString.includes("-")
      ) {
        timeString = dateTimeString; // Already just time
      } else {
        // Parse as full date and extract time
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      // Convert 24-hour to 12-hour format
      if (timeString.includes(":")) {
        const [hours, minutes] = timeString.split(":");
        const hour24 = parseInt(hours);
        const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
        const ampm = hour24 >= 12 ? "PM" : "AM";
        return `${hour12}:${minutes.padStart(2, "0")} ${ampm}`;
      }

      return dateTimeString;
    } catch (error) {
      return dateTimeString; // Return original if conversion fails
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header Card with Image Overlay */}
        <div className="overflow-hidden border border-border bg-card shadow-lg">
          {/* Large Meal Image with Overlay Text */}
          {mealImage ? (
            <div className="relative h-64 w-full">
              {/* Background Image */}
              <img
                src={mealImage}
                alt="Meal"
                className="h-full w-full object-cover"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

              {/* Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background/20 backdrop-blur-sm transition-colors hover:bg-background/40 dark:bg-white/20 dark:hover:bg-white/40"
              >
                <ArrowLeftOutlined className="text-lg text-white" />
              </button>

              {/* Meal Info Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-end justify-between">
                  <div>
                    <h1 className="mb-1 text-2xl font-bold text-white">
                      {mealType || "Breakfast"}
                    </h1>
                    <p className="text-sm text-white/90">
                      {filteredItems.length > 0 && formatTime(filteredItems[0])}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">
                      {displayTotals
                        ? Math.round(displayTotals.calorie_content_kcal)
                        : 0}
                    </div>
                    <div className="text-sm text-white/90">kcal</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Fallback Header without Image */
            <div className="flex items-center p-4">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-muted"
              >
                <ArrowLeftOutlined className="text-lg text-foreground" />
              </button>
              <h1 className="flex-1 text-xl font-bold text-foreground">
                {mealType || "Breakfast"}
              </h1>
              <div className="text-right">
                <div className="text-2xl font-bold text-orange-600">
                  {displayTotals
                    ? Math.round(displayTotals.calorie_content_kcal)
                    : 0}
                </div>
                <div className="text-sm text-muted-foreground">kcal</div>
              </div>
            </div>
          )}
        </div>

        {/* Total Nutrition */}
        {displayTotals && (
          <div className="rounded-2xl border border-border bg-card p-5 shadow-lg">
            <h2 className="mb-4 text-lg font-bold text-foreground">
              Total Nutrition
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Calories */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Calories
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {Math.round(displayTotals.calorie_content_kcal)}
                  </div>
                </div>

                {/* Carbs */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Carbs
                  </div>
                  <div className="font- text-xl text-teal-600">
                    {Math.round(displayTotals.carbohydrate_content_g)}g
                  </div>
                </div>

                {/* Sugar */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Sugar
                  </div>
                  <div className="text-xl font-bold text-yellow-600">
                    {Math.round(displayTotals.sugar_g || 0)}g
                  </div>
                </div>

                {/* GI (avg) */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    GI (avg)
                  </div>
                  <div className="text-xl font-bold text-purple-600">
                    {Math.round(displayTotals.GI_estimate || 0)}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Protein */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Protein
                  </div>
                  <div className="text-xl font-bold text-red-500">
                    {Math.round(displayTotals.protein_content_g)}g
                  </div>
                </div>

                {/* Fat */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Fat
                  </div>
                  <div className="text-xl font-bold text-orange-600">
                    {Math.round(displayTotals.fat_content_g)}g
                  </div>
                </div>

                {/* Fiber */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    Fiber
                  </div>
                  <div className="text-xl font-bold text-orange-500">
                    {Math.round(displayTotals.dietary_fiber_g || 0)}g
                  </div>
                </div>

                {/* GL */}
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    GL
                  </div>
                  <div className="text-xl font-bold text-purple-500">
                    {Math.ceil(displayTotals.GL_estimate || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Food Items Heading */}
        <h3 className="px-1 text-lg font-bold text-foreground">Food Items</h3>

        {/* Food Items List */}
        <div className="space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
              <div className="text-foreground">Loading meal items...</div>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
              <div className="text-red-500">{error}</div>
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg"
              >
                {/* Food Item Header */}
                <div
                  className="cursor-pointer p-4 transition-colors hover:bg-muted"
                  onClick={() => handleItemClick(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <h4 className="mb-1 text-lg font-bold text-foreground">
                          {item.name}
                        </h4>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-semibold text-blue-600">
                            {Math.round(item.calorie_content_kcal)} cal
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground">
                            1 Serving
                          </span>
                        </div>
                      </div>
                    </div>
                    <RightOutlined
                      className={`text-muted-foreground transition-transform duration-200 ${
                        expandedItem === index ? "rotate-90" : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Accordion Content - Nutrition Facts */}
                {expandedItem === index && (
                  <div className="border-t border-border bg-muted p-4">
                    <div className="rounded-xl bg-card p-4 shadow-sm">
                      <h5 className="mb-3 font-semibold text-foreground">
                        Nutrition Facts
                      </h5>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Calories
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.calorie_content_kcal)} Cal
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Total Fats
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.fat_content_g)}g
                          </span>
                        </div>
                        {item.trans_fat_g !== undefined && (
                          <div className="flex justify-between pl-4">
                            <span className="text-sm text-muted-foreground">
                              Trans Fats
                            </span>
                            <span className="text-sm text-foreground">
                              {Math.round(item.trans_fat_g)}g
                            </span>
                          </div>
                        )}
                        {item.saturated_fat_g !== undefined && (
                          <div className="flex justify-between pl-4">
                            <span className="text-sm text-muted-foreground">
                              Saturated Fats
                            </span>
                            <span className="text-sm text-foreground">
                              {Math.round(item.saturated_fat_g)}g
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Cholesterol
                          </span>
                          <span className="font-medium text-foreground">
                            0g
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Net Carbs
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.net_carbs_g)}g
                          </span>
                        </div>
                        <div className="flex justify-between pl-4">
                          <span className="text-sm text-muted-foreground">
                            Dietary Fibers
                          </span>
                          <span className="text-sm text-foreground">
                            {Math.round(item.dietary_fiber_g)}g
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Total GI
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.GI_estimate)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Total GL
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.GL_estimate)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Proteins
                          </span>
                          <span className="font-medium text-foreground">
                            {Math.round(item.protein_content_g)}g
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="border border-border bg-card p-8 text-center shadow-lg">
              <div className="text-muted-foreground">
                No food items in this meal
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default MealDetailPage;
